import { createElement, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import { useModal } from "../../../context/ModalProvider";
import { useToast } from "../../../context/ToastProvider";
import { requestPlannerRefresh } from "../../../hooks/usePlannedWorkouts";
import { localTodayYmd } from "../../../lib/dates";
import type { PendingToast } from "../../../lib/pendingToast";
import { removeTrainingPlan } from "../../../lib/trainingPlanApi";
import { ConnectorPostRaceModal } from "../../Connections/ConnectorPostRaceModal";
import { formatYmdShort } from "../wizard/formatting";
import { useConfirmClick } from "./useConfirmClick";
import { useEventSetup, type RaceJourneyOutcome } from "./useEventSetup";
import type { WizardApplyBody, WizardFinishOutcome } from "../TrainingPlanWizard";

/** What a finished wizard says (always a success tone — failures throw). */
export type WizardFinishNotice = PendingToast & { tone: "success" };

export type WizardApplyResult =
  /** The athlete backed out of a nested sheet — keep the wizard open. */
  | { kind: "stay_open" }
  /** Applied (journey / plan-less race / tune-up) — say this and move on. */
  | { kind: "done"; notice: WizardFinishNotice };

/**
 * The ONE mapper from a race-apply outcome to its toast. Pure.
 *
 * Throws when a plan apply reports no applied block: with a `planId` that is
 * a real failure the wizard should surface, not a quiet success.
 */
export function describeWizardApplyOutcome(
  outcome: RaceJourneyOutcome,
  body: WizardApplyBody
): WizardApplyResult {
  // Cancelled the overlap-resolution sheet → the wizard STAYS OPEN, so a
  // dismiss never closes it back onto whatever launched it. `event_only` is
  // unreachable from the wizard apply (the funnel only offers that exit on
  // E3/E4 entries — B2), but the union carries it; treat it the same way.
  if (outcome.kind === "cancelled" || outcome.kind === "event_only") return { kind: "stay_open" };
  const raceName = body.raceName?.trim() || "Your race";
  // `nest_new` resolution: the race was saved as a tune-up riding an existing
  // build — a success (B1) with no journey and no plan. Say so honestly.
  if (outcome.kind === "milestone") {
    return {
      kind: "done",
      notice: {
        tone: "success",
        message: `${raceName} added as a prep race${
          outcome.nestedIntoName ? ` inside your ${outcome.nestedIntoName} training plan` : ""
        } — it rides that plan, so no separate one was added.`,
        durationMs: 10_000
      }
    };
  }
  const { applied } = outcome.created;
  if (!applied) {
    // Plan-less race journey (no planId) — expected to have no applied block.
    // With a planId it's a real failure.
    if (body.planId) throw new Error("Couldn't apply the plan — refresh and try again.");
    const message =
      body.trainingChoice === "connector"
        ? body.connectorLinked
          ? `${raceName} added — your training app's workouts will sync onto your calendar.`
          : `${raceName} added — connect your training app to import workouts.`
        : body.hasUpcomingWorkouts
          ? `${raceName} added — we'll fuel the workouts on your calendar.`
          : `${raceName} added — add your own workouts and we'll fuel them.`;
    return { kind: "done", notice: { tone: "success", message, durationMs: 8000 } };
  }
  const eventName = body.raceName?.trim() || applied.event?.name || "Goal race";
  const planType = body.planTypeLabel ?? "Training";
  return {
    kind: "done",
    notice: {
      tone: "success",
      message: `${planType} training plan for ${eventName} added — ${applied.created.length} sessions through race day.`,
      durationMs: 10_000
    }
  };
}

/** Unlinked connector choice — the connect modal, then Dashboard Connections. */
function needsConnectHandoff(outcome: WizardFinishOutcome): boolean {
  return outcome.trainingChoice === "connector" && !outcome.connectorLinked;
}

/**
 * Where a finished wizard sends the athlete. Pure. The chosen method's
 * handoff (D16/D17) wins; otherwise the ONE landing rule (D5/D6): upcoming
 * workouts → My Fuel, none → the My Training calendar to add them.
 *
 *  - connector, not linked → Dashboard Connections, OAuth returning to My Training
 *  - import   → My Training, import review attached to the new journey
 *  - generate → My Training, balanced-week generator open
 */
export function wizardFinishHref(outcome: WizardFinishOutcome): string {
  if (needsConnectHandoff(outcome)) {
    return "/dashboard?connectReturn=my_training#connections-section";
  }
  if (outcome.trainingChoice === "import") {
    return outcome.journey
      ? `/my-training?import=1&journey=${encodeURIComponent(outcome.journey.id)}`
      : "/my-training?import=1";
  }
  if (outcome.trainingChoice === "generate") return "/my-training?generate=1";
  return outcome.hasUpcomingWorkouts ? "/my-fuel" : "/my-training";
}

/**
 * The rolling finish's toast. Anchor P5: the server anchors at the next
 * fuel-eligible workout, which can postdate today — say so, or a future-dated
 * first box reads as broken. Landing on an empty calendar nudges to add
 * workouts. Null when the create returned no anchor. Pure.
 */
export function describeRollingFinish(
  outcome: WizardFinishOutcome,
  todayYmd: string
): WizardFinishNotice | null {
  const anchor = outcome.journey?.startYmd;
  if (!anchor) return null;
  if (anchor > todayYmd) {
    return {
      tone: "success",
      message: `Fueling starts ${formatYmdShort(anchor)}`,
      detail: "Your first box opens with your next planned workout."
    };
  }
  return {
    tone: "success",
    message: "Fueling starts today",
    detail:
      wizardFinishHref(outcome) === "/my-training"
        ? "Add your workouts and we'll fuel them."
        : undefined
  };
}

export type AppliedPlanBanner = { planGroupId: string; message: string };

/**
 * The ONE host for a finished onboarding wizard — landing doors, storefront
 * embed and the planner screens all use it, so the toast, the training-method
 * handoff and the landing can never drift between them.
 *
 *  - `onApply` (wizard prop): runs the shared event funnel
 *    (`useEventSetup.applyWizardPlan` — profile-first PATCH + journey create +
 *    anon race draft), then finishes. Returns `false` when the wizard should
 *    stay open (overlap sheet dismissed).
 *  - `onRollingCreated` (wizard prop): finishes a rolling create.
 *
 * A finish toasts, asks any mounted calendar to refresh (+ `onFinished`), then
 * runs `wizardFinishHref` — via the connect modal for an unlinked connector.
 * A Fuel Goods plan apply also gets a "Remove plan" toast action and sets
 * `appliedPlanBanner` (a host that stays mounted renders it with
 * `removePlanConfirm`).
 *
 * `notify` replaces the in-page toast for a host that leaves the page right
 * after (the storefront embed stashes a pending toast for the SPA to drain);
 * such a host gets no Remove action or banner.
 */
export function useWizardFinish(opts?: {
  notify?: (notice: WizardFinishNotice) => void;
  /** Host refreshes after a finish or a plan removal (planner: events + dashboard). */
  onFinished?: () => void;
}): {
  onApply: (body: WizardApplyBody) => Promise<void | false>;
  onRollingCreated: (outcome: WizardFinishOutcome) => void;
  appliedPlanBanner: AppliedPlanBanner | null;
  removePlanConfirm: ReturnType<typeof useConfirmClick>;
} {
  const navigate = useNavigate();
  const { push: pushToast, update: updateToast } = useToast();
  const { open: openModal, close: closeModal } = useModal();
  const { state: authState, refreshCsrf } = useAuth();
  const { applyWizardPlan } = useEventSetup();
  // Latest opts / CSRF without re-binding the callbacks (hosts pass inline
  // functions; a toast action can fire after a token refresh).
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const csrfRef = useRef<string | null>(null);
  csrfRef.current =
    authState.kind === "customer" || authState.kind === "anon" ? authState.csrfToken : null;
  const [appliedPlanBanner, setAppliedPlanBanner] = useState<AppliedPlanBanner | null>(null);

  const refreshAfterChange = useCallback(() => {
    requestPlannerRefresh();
    optsRef.current?.onFinished?.();
  }, []);

  const removePlan = useCallback(
    async (planGroupId: string) => {
      const csrf = csrfRef.current;
      if (!csrf) throw new Error("Couldn't remove the plan");
      await removeTrainingPlan(planGroupId, csrf, refreshCsrf);
      setAppliedPlanBanner((b) => (b?.planGroupId === planGroupId ? null : b));
      refreshAfterChange();
    },
    [refreshCsrf, refreshAfterChange]
  );

  const finish = useCallback(
    (outcome: WizardFinishOutcome, notice: WizardFinishNotice | null, appliedPlanGroupId?: string) => {
      const notify = optsRef.current?.notify;
      if (notice && notify) notify(notice);
      else if (notice) {
        pushToast({
          ...notice,
          action: appliedPlanGroupId
            ? {
                label: "Remove plan",
                onClick: (toastId) => {
                  // Reuse the same toast: spinner while removing, then resolve
                  // in place (update — a second push would render a duplicate).
                  updateToast(toastId, {
                    tone: "pending",
                    message: "Removing plan…",
                    action: undefined,
                    durationMs: 0
                  });
                  removePlan(appliedPlanGroupId).then(
                    () => updateToast(toastId, { tone: "info", message: "Plan removed" }),
                    () => updateToast(toastId, { tone: "error", message: "Couldn't remove the plan" })
                  );
                }
              }
            : undefined
        });
      }
      refreshAfterChange();
      const href = wizardFinishHref(outcome);
      if (!needsConnectHandoff(outcome)) {
        navigate(href);
        return;
      }
      const go = () => {
        closeModal(id);
        navigate(href);
      };
      const id = openModal(
        createElement(ConnectorPostRaceModal, {
          providerLabels: outcome.connectorOfferLabels ?? [],
          raceName: outcome.journey?.raceName,
          onClose: go,
          onContinue: go
        })
      );
    },
    [pushToast, updateToast, removePlan, refreshAfterChange, navigate, openModal, closeModal]
  );

  const onApply = useCallback(
    async (body: WizardApplyBody) => {
      // The funnel guards CSRF against a fresh token (read from a ref at call
      // time), so no stale capture-time guard is needed here.
      const outcome = await applyWizardPlan(body);
      const result = describeWizardApplyOutcome(outcome, body);
      if (result.kind === "stay_open") return false;
      const created = outcome.kind === "journey" ? outcome.created : null;
      const applied = created?.applied ?? null;
      if (applied && !optsRef.current?.notify) {
        setAppliedPlanBanner({ planGroupId: applied.planGroupId, message: result.notice.message });
      }
      finish(
        {
          kind: "race",
          journey: created
            ? {
                id: created.journey.id,
                planGroupId: created.journey.planGroupId,
                raceName: body.raceName?.trim() || null,
                raceYmd: created.journey.raceYmd,
                startYmd: created.journey.startYmd
              }
            : null,
          trainingChoice: body.trainingChoice,
          connectorLinked: body.connectorLinked,
          connectorOfferLabels: body.connectorOfferLabels,
          // A Fuel Goods plan apply just put its sessions on the calendar.
          hasUpcomingWorkouts: applied != null || body.hasUpcomingWorkouts === true
        },
        result.notice,
        applied?.planGroupId
      );
    },
    [applyWizardPlan, finish]
  );

  const onRollingCreated = useCallback(
    (outcome: WizardFinishOutcome) => finish(outcome, describeRollingFinish(outcome, localTodayYmd())),
    [finish]
  );

  // Persistent banner Remove — same removal as the toast action, from the
  // always-visible banner (two-step inline confirm so a stray click doesn't
  // drop the block).
  const removePlanConfirm = useConfirmClick(async () => {
    if (!appliedPlanBanner) return;
    try {
      await removePlan(appliedPlanBanner.planGroupId);
      pushToast({ tone: "info", message: "Plan removed" });
    } catch {
      pushToast({ tone: "error", message: "Couldn't remove the plan" });
    }
  });

  return { onApply, onRollingCreated, appliedPlanBanner, removePlanConfirm };
}
